'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FileText, Phone } from 'lucide-react'

import { resolveClientCopy } from '@/lib/client-copy'
import { useSiteSettings } from '@/lib/site-settings-context'
import { formatPhone } from '@/lib/utils'

import { usePhoneClickTracker } from './phone-link'

/**
 * Fixed bottom mobile CTA, offering both actions at once.
 *
 * It used to show one action picked at random per navigation, which meant half
 * of mobile visitors never saw the quote link and the split could not be read
 * in analytics. Both are now always present.
 *
 * The bar also hides itself whenever a real quote, call, or enquiry action is
 * already on screen. It sits over the bottom 5rem of the viewport permanently, and on a
 * long hero (rock-walls at 375x667) that band lands exactly on the hero's own
 * buttons, so the primary CTA was arriving bisected or below the fold. The
 * layout's bottom padding only protects the end of the document, not whatever
 * happens to be at the fold on load.
 *
 * `position: fixed` (not sticky) because mobile browsers with dynamic chrome,
 * especially Firefox Android with its bottom URL bar, mis-position sticky
 * bottom elements. Fixed anchors to the visual viewport, immune to ancestor
 * overflow quirks and toolbar motion.
 */
export function StickyMobileCta() {
  const { phoneNumber, copy } = useSiteSettings()
  const { siteLabels } = resolveClientCopy(copy)
  const trackPhoneClick = usePhoneClickTracker()
  const pathname = usePathname()
  const [ctaOnScreen, setCtaOnScreen] = useState(false)

  useEffect(() => {
    // Scoped to `main` on purpose: the header and footer sit outside it, so
    // their permanent phone links can never suppress the bar. `^=` catches
    // /quote?audience=trade alongside the plain link; the exact matches catch
    // the rock-walls page's inline #enquire anchor and its landing form.
    const actions = document.querySelectorAll<HTMLElement>(
      'main a[href^="/quote"], main a[href^="tel:"], main a[href="#enquire"], main #enquire'
    )

    const visible = new Set<Element>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target)
          else visible.delete(entry.target)
        }
        setCtaOnScreen(visible.size > 0)
      },
      // Ignore the band the bar itself covers, otherwise it would count a
      // button it is sitting on top of as visible and never get out of the
      // way. rootMargin takes px or %, never rem, so this tracks the 5rem
      // bar height by hand.
      { rootMargin: '0px 0px -80px 0px' }
    )
    for (const action of actions) observer.observe(action)
    // Reset on the way out rather than on the way in: a page with no in-page
    // actions at all registers no observer, so without this the bar would stay
    // hidden after navigating to it from a page that had one.
    return () => {
      observer.disconnect()
      setCtaOnScreen(false)
    }
  }, [pathname])

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 transition-opacity duration-300 ease-out motion-reduce:transition-none md:hidden"
      style={{
        opacity: ctaOnScreen ? 0 : 1,
        pointerEvents: ctaOnScreen ? 'none' : 'auto',
      }}
      aria-hidden={ctaOnScreen}
    >
      <div className="divider-glow h-px" />
      {/*
        Bottom padding includes `env(safe-area-inset-bottom)` so the dark
        chrome bleeds all the way to the bottom of the viewport on Android
        edge-to-edge and iOS notched devices. Previously the safe-area
        padding sat on the outer wrapper, leaving a transparent gap below
        the bar on Android Chrome and Firefox.
      */}
      <div
        className="bg-[#1a0e20]/95 px-4 pt-3 backdrop-blur-sm"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex h-12 items-stretch gap-2">
          <Link
            href={pathname === '/rock-walls' ? '#enquire' : '/quote'}
            className="bg-brand-purple flex flex-1 items-center justify-center gap-2 rounded-sm shadow-[3px_3px_0px_rgba(0,0,0,0.3)] active:shadow-[1px_1px_0px_rgba(0,0,0,0.3)]"
            style={{ transform: 'rotate(0.4deg)' }}
            tabIndex={ctaOnScreen ? -1 : 0}
          >
            <FileText className="size-5 text-white" aria-hidden="true" />
            <span className="font-marker text-xl tracking-wide text-white">
              {siteLabels.stickyCtaLabel}
            </span>
          </Link>
          <a
            href={`tel:${phoneNumber}`}
            className="bg-brand-green flex w-14 shrink-0 items-center justify-center rounded-sm shadow-[3px_3px_0px_rgba(0,0,0,0.3)] active:shadow-[1px_1px_0px_rgba(0,0,0,0.3)]"
            style={{ transform: 'rotate(-0.4deg)' }}
            tabIndex={ctaOnScreen ? -1 : 0}
            aria-label={`Call Rock'n Earthworks on ${formatPhone(phoneNumber)}`}
            onClick={trackPhoneClick}
          >
            <Phone className="size-6 text-[#1a0e20]" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  )
}
